import React, { useState, useEffect, useRef } from 'react';
import { MicrophoneIcon, PauseIcon, XMarkIcon } from '../../icons';
import { useNotification } from '../../contexts/NotificationContext';
import { recordingAPI } from '../../services/api';
import CaseReferenceField from '../recording/CaseReferenceField';
import LanguageSelect from '../recording/LanguageSelect';
import SessionMetadataBar from '../recording/SessionMetadataBar';
import LanguageChangeMarkersPanel from '../recording/LanguageChangeMarkersPanel';
import { formatRecordingTime } from '../../utils/recordingForm';

const apiErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const RecordingModal = ({
  open,
  onClose,
  tenantId,
  courtDisplayName,
  initiatorDisplayName,
  languages,
  onUploadSuccess,
  onDuplicateSession: _onDuplicateSession,
}) => {
  const {
    showProcessingNotification,
    showSuccessNotification,
    showErrorNotification,
    updateNotification,
  } = useNotification();

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingTitle, setRecordingTitle] = useState('');
  const [caseReference, setCaseReference] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [languageMarkers, setLanguageMarkers] = useState([]);
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  const [currentRecordingId, setCurrentRecordingId] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [serverFinalized, setServerFinalized] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const chunksRef = useRef([]);
  const mimeTypeRef = useRef('audio/webm');

  const canStartRecordingSession =
    recordingTitle.trim().length > 0 &&
    caseReference.trim().length > 0 &&
    selectedLanguage.length > 0 &&
    Boolean(tenantId);

  const isLiveSession = Boolean(sessionStartedAt && currentRecordingId);

  const sessionStatusLabel = isUploadingAudio
    ? 'Uploading'
    : isRecording
      ? (isPaused ? 'Paused' : 'Live')
      : isLiveSession
        ? (serverFinalized ? 'Stopped' : 'Stopped')
        : '';

  const resetRecordingForm = () => {
    setCaseReference('');
    setSelectedLanguage('');
    setLanguageMarkers([]);
    setSessionStartedAt(null);
    setRecordingTitle('');
    setCurrentRecordingId(null);
    setRecordedAudio(null);
    setAudioChunks([]);
    chunksRef.current = [];
    setMediaRecorder(null);
    setRecordingTime(0);
    setIsRecording(false);
    setIsPaused(false);
    setServerFinalized(false);
    setLifecycleBusy(false);
  };

  useEffect(() => {
    if (!open) return undefined;
    let interval = null;
    if (isRecording && !isPaused) {
      interval = setInterval(() => {
        setRecordingTime((time) => time + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [open, isRecording, isPaused]);

  useEffect(() => {
    return () => {
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
    };
  }, [mediaRecorder]);

  const stopMediaAndGetBlob = () =>
    new Promise((resolve) => {
      const recorder = mediaRecorder;
      if (!recorder || (recorder.state !== 'recording' && recorder.state !== 'paused')) {
        const existing =
          recordedAudio ||
          (chunksRef.current.length
            ? new Blob(chunksRef.current, { type: mimeTypeRef.current })
            : null);
        resolve(existing);
        return;
      }

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
        setRecordedAudio(blob);
        setAudioChunks([...chunksRef.current]);
        try {
          recorder.stream?.getTracks?.().forEach((track) => track.stop());
        } catch {
          /* ignore */
        }
        resolve(blob);
      };
      recorder.stop();
      setIsRecording(false);
      setIsPaused(false);
    });

  const finalizeOnServer = async (audioBlob, durationSeconds) => {
    if (!tenantId || !currentRecordingId) {
      throw new Error('Missing tenant or session');
    }
    if (serverFinalized) {
      return null;
    }

    const formData = new FormData();
    if (audioBlob && audioBlob.size > 0) {
      const ext = audioBlob.type.includes('mp4')
        ? 'mp4'
        : audioBlob.type.includes('wav')
          ? 'wav'
          : 'webm';
      formData.append(
        'audio',
        audioBlob,
        `${(recordingTitle || 'recording').trim() || 'recording'}.${ext}`
      );
    }
    formData.append('duration_seconds', String(durationSeconds || 0));

    const resp = await recordingAPI.stopSession(tenantId, currentRecordingId, formData);
    setServerFinalized(true);
    return resp?.data?.data;
  };

  const handleStartRecordingSession = async () => {
    if (!canStartRecordingSession) {
      showErrorNotification(
        'Missing information',
        !tenantId
          ? 'Your account is not assigned to a court tenant.'
          : 'Enter a title, case reference, and language before starting.'
      );
      return;
    }

    setLifecycleBusy(true);
    try {
      const createResp = await recordingAPI.createSession(tenantId, {
        case_reference: caseReference.trim(),
        language: selectedLanguage,
      });
      const session = createResp.data?.data;
      const sessionId = session?.session_id || session?.recording_id;
      if (!sessionId) {
        throw new Error('Server did not return a session_id');
      }

      await recordingAPI.startSession(tenantId, sessionId);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = 'audio/wav';
          }
        }
      }
      mimeTypeRef.current = mimeType;
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream, { mimeType });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
          setAudioChunks([...chunksRef.current]);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecordedAudio(blob);
        stream.getTracks().forEach((track) => track.stop());
      };

      recorder.start(1000);
      setMediaRecorder(recorder);
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      setSessionStartedAt(new Date());
      setLanguageMarkers([]);
      setCurrentRecordingId(sessionId);
      setServerFinalized(false);
    } catch (error) {
      showErrorNotification(
        'Could not start recording',
        apiErrorMessage(error, 'Failed to create or start the recording session.')
      );
    } finally {
      setLifecycleBusy(false);
    }
  };

  const handlePauseRecording = async () => {
    if (!mediaRecorder || mediaRecorder.state !== 'recording' || !tenantId || !currentRecordingId) {
      return;
    }
    setLifecycleBusy(true);
    try {
      await recordingAPI.pauseSession(tenantId, currentRecordingId);
      mediaRecorder.pause();
      setIsPaused(true);
    } catch (error) {
      showErrorNotification(
        'Pause failed',
        apiErrorMessage(error, 'Could not pause the recording session.')
      );
    } finally {
      setLifecycleBusy(false);
    }
  };

  const handleResumeRecording = async () => {
    if (!mediaRecorder || mediaRecorder.state !== 'paused' || !tenantId || !currentRecordingId) {
      return;
    }
    setLifecycleBusy(true);
    try {
      await recordingAPI.resumeSession(tenantId, currentRecordingId);
      mediaRecorder.resume();
      setIsPaused(false);
    } catch (error) {
      showErrorNotification(
        'Resume failed',
        apiErrorMessage(error, 'Could not resume the recording session.')
      );
    } finally {
      setLifecycleBusy(false);
    }
  };

  const handleStopAndFinalize = async ({ closeAfter = true } = {}) => {
    if (!currentRecordingId || serverFinalized) {
      if (closeAfter) {
        onClose();
        resetRecordingForm();
      }
      return;
    }

    const savedDuration = recordingTime;
    setIsUploadingAudio(true);
    setLifecycleBusy(true);

    const notificationId = showProcessingNotification(
      'Finalizing recording',
      'Stopping capture and queuing transcription...'
    );

    try {
      const audioBlob = await stopMediaAndGetBlob();
      const savedCase = caseReference;
      const savedTitle = recordingTitle;
      const savedSessionId = currentRecordingId;
      await finalizeOnServer(audioBlob, savedDuration);

      updateNotification(notificationId, {
        type: 'success',
        title: 'Recording Queued',
        message: 'Session stopped. Audio saved; English transcription is being generated.',
        icon: '✅',
        color: 'green',
        duration: 5000,
      });
      onUploadSuccess?.({
        id: savedSessionId,
        session_id: savedSessionId,
        case_reference: savedCase,
        title: savedTitle || savedCase,
        status: 'uploading',
        created_at: new Date().toISOString(),
      });
      if (closeAfter) {
        onClose();
        resetRecordingForm();
      }
    } catch (error) {
      updateNotification(notificationId, {
        type: 'error',
        title: 'Stop failed',
        message: apiErrorMessage(error, 'Failed to finalize the recording session.'),
        icon: '❌',
        color: 'red',
        duration: 7000,
      });
    } finally {
      setIsUploadingAudio(false);
      setLifecycleBusy(false);
    }
  };

  const handleStopRecording = () => {
    handleStopAndFinalize({ closeAfter: false });
  };

  const handleSaveRecording = () => {
    if (serverFinalized) {
      showSuccessNotification('Already sent', 'This session was already finalized on stop.');
      onClose();
      resetRecordingForm();
      return;
    }
    handleStopAndFinalize({ closeAfter: true });
  };

  const handleCancelRecording = () => {
    if (mediaRecorder?.state === 'recording' || mediaRecorder?.state === 'paused') {
      try {
        mediaRecorder.stop();
      } catch {
        /* ignore */
      }
    }
    onClose();
    resetRecordingForm();
  };

  if (!open) return null;

  const controlsDisabled = isUploadingAudio || lifecycleBusy;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-legal-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg sm:text-xl font-semibold text-legal-900">
              {!isLiveSession
                ? 'Start Recording'
                : isRecording
                  ? (isPaused ? 'Recording Paused' : 'Recording in Progress')
                  : 'Review & Send Recording'}
            </h3>
            <button type="button" onClick={handleCancelRecording} className="text-legal-400 hover:text-legal-600">
              <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {!isLiveSession ? (
            <div className="space-y-4">
              <CaseReferenceField value={caseReference} onChange={setCaseReference} />
              <LanguageSelect value={selectedLanguage} onChange={setSelectedLanguage} languages={languages} />
              <div>
                <label className="block text-sm font-medium text-legal-700 mb-2">
                  Recording Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={recordingTitle}
                  onChange={(e) => setRecordingTitle(e.target.value)}
                  placeholder="Enter recording title..."
                  className="w-full px-3 py-2 border border-legal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-court-500 focus:border-court-500 text-sm"
                />
              </div>

              <SessionMetadataBar
                caseReference={caseReference}
                language={selectedLanguage}
                courtName={courtDisplayName}
                initiatorName={initiatorDisplayName}
              />

              <div className="text-center py-2">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MicrophoneIcon className="h-10 w-10 text-red-600" />
                </div>
                <p className="text-sm text-legal-600 mb-4">
                  Complete case reference and language, then start recording.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleCancelRecording}
                  className="flex-1 px-4 py-2 text-legal-600 border border-legal-300 rounded-lg hover:bg-legal-100 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleStartRecordingSession}
                  disabled={!canStartRecordingSession || lifecycleBusy}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {lifecycleBusy ? 'Starting...' : 'Start Recording'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <SessionMetadataBar
                caseReference={caseReference}
                language={selectedLanguage}
                courtName={courtDisplayName}
                initiatorName={initiatorDisplayName}
                startedAt={sessionStartedAt}
                status={sessionStatusLabel}
              />

              <div className="text-center py-2">
                <div
                  className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    isPaused ? 'bg-yellow-500' : isRecording ? 'bg-red-500 animate-pulse' : 'bg-gray-500'
                  }`}
                >
                  {isPaused ? (
                    <PauseIcon className="h-10 w-10 text-white" />
                  ) : (
                    <MicrophoneIcon className="h-10 w-10 text-white" />
                  )}
                </div>
                <h4 className="text-lg font-semibold text-legal-900 mb-2">
                  {recordingTitle || 'Untitled Recording'}
                </h4>
                <div
                  className={`text-3xl font-mono font-bold mb-2 ${
                    isPaused ? 'text-yellow-600' : isRecording ? 'text-red-600' : 'text-gray-600'
                  }`}
                >
                  {formatRecordingTime(recordingTime)}
                </div>
                <p className="text-sm text-legal-600">
                  {isUploadingAudio
                    ? 'Uploading audio to server...'
                    : isPaused
                      ? 'Recording paused...'
                      : isRecording
                        ? 'Recording audio from microphone...'
                        : serverFinalized
                          ? 'Recording finalized and queued.'
                          : 'Recording stopped. Send when ready.'}
                </p>
              </div>

              {isRecording && (
                <LanguageChangeMarkersPanel
                  markers={languageMarkers}
                  onAddMarker={(marker) => setLanguageMarkers((prev) => [...prev, marker])}
                  currentTimeSeconds={recordingTime}
                  languages={languages}
                  disabled={controlsDisabled}
                />
              )}

              <div className="flex space-x-2">
                {isRecording && (
                  <button
                    type="button"
                    onClick={isPaused ? handleResumeRecording : handlePauseRecording}
                    disabled={controlsDisabled}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm disabled:opacity-50"
                  >
                    {isPaused ? 'Resume' : 'Pause'}
                  </button>
                )}
                {isRecording && (
                  <button
                    type="button"
                    onClick={handleStopRecording}
                    disabled={controlsDisabled}
                    className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm disabled:opacity-50"
                  >
                    Stop Recording
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSaveRecording}
                  disabled={controlsDisabled}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                >
                  {isUploadingAudio ? 'Uploading...' : serverFinalized ? 'Done' : 'Send Recording'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordingModal;
